import { requiredMonthlyContribution } from './goals.js';
import { seriesKeyOf, seriesKeyAliases } from './series-key.js';
import { isMoneyIn } from '../txType.js';
import { plannedProjectionInstances } from './planned.js';

const RECURRING_MONTHLY_OCCURRENCES = {
  daily: 30,
  weekly: 4,
  biweekly: 2,
  monthly: 1,
};

function recurringMonthlyDelta(target, lastInstance) {
  const { date, recurrenceType, amount, type } = lastInstance;
  const monthDiff =
    (target.getFullYear() - date.getFullYear()) * 12 +
    (target.getMonth() - date.getMonth());
  if (monthDiff <= 0) return 0;

  let occurrences = RECURRING_MONTHLY_OCCURRENCES[recurrenceType] || 0;
  if (recurrenceType === 'semiannual') occurrences = monthDiff % 6 === 0 ? 1 : 0;
  if (recurrenceType === 'yearly') occurrences = monthDiff % 12 === 0 ? 1 : 0;
  if (!occurrences) return 0;

  const signed = isMoneyIn(type) ? amount : -amount;
  return signed * occurrences;
}

// Groups recurring transactions by series identity (desc+type). Each entry
// tracks the latest instance's DATE + recurrenceType (the projection anchor)
// and the AVERAGE amount across all instances of the series — so variable
// bills project at their mean rather than whatever the last charge happened
// to be.
function lastInstanceByRecurringSeries(transactions) {
  const map = {};
  transactions
    .filter((t) => t.isRecurring)
    .forEach((t) => {
      const key = seriesKeyOf(t);
      const d = new Date(t.date);
      const amt = Number(t.amount) || 0;
      if (!map[key]) {
        map[key] = {
          date: d,
          recurrenceType: t.recurrenceType,
          type: t.type,
          // Every key a stored scenario delta could name this series by.
          keys: new Set(seriesKeyAliases(t)),
          _sum: amt,
          _count: 1,
        };
      } else {
        seriesKeyAliases(t).forEach((k) => map[key].keys.add(k));
        map[key]._sum += amt;
        map[key]._count += 1;
        if (d > map[key].date) {
          map[key].date = d;
          map[key].recurrenceType = t.recurrenceType;
        }
      }
    });
  Object.values(map).forEach((s) => {
    s.amount = s._sum / s._count;
  });
  return map;
}

function goalActiveInMonth(goal, monthStart) {
  if (!goal.targetDate) return true;
  const target = new Date(goal.targetDate);
  return monthStart <= new Date(target.getFullYear(), target.getMonth(), 1);
}

// Parse a 'YYYY-MM-DD' (or any Date-parseable) value to the first of its month,
// so scale start dates gate projected months at month granularity.
function startOfMonth(value) {
  if (!value) return null;
  const d = value instanceof Date
    ? value
    : (/^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00`) : new Date(value));
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function buildProjection(
  currentBalance,
  transactions,
  monthsAhead = 12,
  options = {},
) {
  // excludeSeriesKeys: series with no future (the user marked them ended).
  const { goals = [], today = new Date(), scaleDeltas = [], planned = [], excludeSeriesKeys = null } = options;
  // Planned series (see planned.js) ride alongside the detected ones under
  // their own keys, anchored a cycle before their first date so that date counts.
  const lastInstanceMap = {
    ...lastInstanceByRecurringSeries(transactions),
    ...plannedProjectionInstances(planned),
  };

  // Index scale modifiers by series key. A scale applies to a projected month
  // when the month is on/after its start (no start = always). Multiple scales
  // on one series compound.
  const scaleBySeries = {};
  scaleDeltas.forEach((s) => {
    if (!s || !s.seriesKey || !Number.isFinite(Number(s.multiplier))) return;
    (scaleBySeries[s.seriesKey] ||= []).push({
      multiplier: Number(s.multiplier),
      from: startOfMonth(s.startDate),
    });
  });
  const activeGoals = goals.filter((g) => !g.archived);
  const goalMonthlyNeeds = activeGoals.map((g) => ({
    goal: g,
    need: requiredMonthlyContribution(g, transactions, today),
  }));

  const months = [];
  let balance = currentBalance;

  for (let i = 1; i <= monthsAhead; i++) {
    const target = new Date(today.getFullYear(), today.getMonth() + i, 1);
    const monthLabel = target.toLocaleDateString('en-US', {
      month: 'short',
      year: '2-digit',
    });

    let monthlyNet = 0;
    Object.entries(lastInstanceMap).forEach(([key, inst]) => {
      if (excludeSeriesKeys && excludeSeriesKeys.has(key)) return;
      let delta = recurringMonthlyDelta(target, inst);
      // Prefer the current key; fall back to whichever legacy alias a scenario
      // saved earlier used, so one scenario can never scale a series twice.
      const scales = scaleBySeries[key]
        || [...inst.keys].map((k) => scaleBySeries[k]).find(Boolean);
      if (scales) {
        scales.forEach((sc) => {
          if (!sc.from || target >= sc.from) delta *= sc.multiplier;
        });
      }
      monthlyNet += delta;
    });

    let goalReserved = 0;
    goalMonthlyNeeds.forEach(({ goal, need }) => {
      if (!Number.isFinite(need) || need <= 0) return;
      if (!goalActiveInMonth(goal, target)) return;
      goalReserved += need;
    });

    balance = parseFloat((balance + monthlyNet - goalReserved).toFixed(2));
    months.push({ label: monthLabel, balance, goalReserved });
  }

  return months;
}
